import { Injectable } from '@angular/core';
import { HttpService } from 'library-explorer';
import { BehaviorSubject, Observable, forkJoin, of, throwError } from 'rxjs';
import { delay, map, retryWhen, switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { SlideBuilderSection } from '@app/model/slide-builder-section';
import { SlideDTO } from '@app/model/slideDTO.model';
import { SlideType } from 'library-explorer';

@Injectable({
  providedIn: 'root'
})
export class ChapterListeningService {

  private listeningEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly SUPPORTED_PARAGRAPHS = [SlideType.SlideText, SlideType.SlideTextFlipcards, SlideType.SlideAccordion, SlideType.Hotspots]

  constructor(
    private readonly httpService: HttpService,
    private readonly http: HttpClient
  ) { }

  public fetchParagraphSpeech(paragraph: SlideDTO): Observable<{url: string}[]> {
    let data: any[]; 
    switch (paragraph.bundle) {
      case SlideType.SlideTextFlipcards:
        data = paragraph.flipcards
          .filter(flipcard => flipcard.frontText || flipcard.backText)
          .reduce((prev, flipcard) => [
            ...prev,
            flipcard.frontText && {
              url: `paragraphs/${flipcard.id}/text-to-speech?field=field_paragraph_main_text`,
              additionalData: { side: 'front', id: flipcard.id }
            },
            flipcard.backText && {
              url: `paragraphs/${flipcard.id}/text-to-speech?field=field_paragraph_secondary_text`,
              additionalData: { side: 'back', id: flipcard.id }
            }
          ].filter(x => x), []);
        break;
      case SlideType.SlideAccordion:
        data = paragraph.accordion
          .reduce((prev, accordion) => [
            ...prev,
            {
              url: `paragraphs/${accordion.id}/text-to-speech`,
              additionalData: { id: accordion.id }
            }
          ].filter(x => x), []);
        break;
      case SlideType.Hotspots:
        data = paragraph.hotspots?.clickspots
          .filter(clickspot => clickspot.text)
          .reduce((prev, clickspot) => [
            ...prev,
            {
              url: `paragraphs/${clickspot.id}/text-to-speech`,
              additionalData: { id: clickspot.id }
            }
          ].filter(x => x), []);
        break;
      default:
        data = [{url: `paragraphs/${paragraph.id}/text-to-speech`}];
    }

    return forkJoin(
      data.map(item => this.httpService.get<any>(item.url).pipe(
        map(res => ({...res, additionalData: item.additionalData}))
      ))
    );
  }

  public pollFile(url: string): Observable<any> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      retryWhen(errors => errors.pipe(
        switchMap((err) => {
          if (err.status === 404) {
            return of(err).pipe(delay(1000));
          }

          return throwError(err);
        }),
        take(20)
      ))
    );
  }

  public isEnabled(): Observable<boolean> {
    return this.listeningEnabled$.asObservable();
  }

  public isEnabledValue(): boolean {
    return this.listeningEnabled$.value;
  }

  public setStatus(status: boolean): void {
    this.listeningEnabled$.next(status);
  }

  public getSupportedParagraphs(sections: SlideBuilderSection[]): SlideDTO[] {
    if (!sections || !Array.isArray(sections)) {
      return [];
    }

    return sections?.reduce((prev, section) => {
      const { items = [], left_columns_items = [], right_columns_items = [] } = section;

      prev = [
        ...prev,
        ...items.filter(slide => this.SUPPORTED_PARAGRAPHS.indexOf(slide.bundle) !== -1),
        ...left_columns_items.filter(slide => this.SUPPORTED_PARAGRAPHS.indexOf(slide.bundle) !== -1),
        ...right_columns_items.filter(slide => this.SUPPORTED_PARAGRAPHS.indexOf(slide.bundle) !== -1),
      ]
      return prev;
    }, []);
  }
}
